import React, { useState, useEffect, useRef } from 'react';
import { 
  Folder, 
  File, 
  Image as ImageIcon, 
  Upload, 
  Trash2, 
  Search, 
  Grid, 
  List,
  ChevronRight,
  ChevronDown,
  FolderPlus,
  RefreshCw,
  Home,
  ArrowLeft,
  ArrowUp
} from 'lucide-react';

const FileManager = ({ credentials, backendUrl }) => {
  const [currentPath, setCurrentPath] = useState('/');
  const [fileStructure, setFileStructure] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [stats, setStats] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root']));
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDirectory(currentPath);
    loadStats();
  }, [currentPath]);

  const loadDirectory = async (path) => {
    setLoading(true);
    setError('');
    try {
      // Add cache-busting timestamp
      const timestamp = Date.now();
      const response = await fetch(
        `${backendUrl}/api/admin/files/list?path=${encodeURIComponent(path)}&recursive=true&_t=${timestamp}`,
        {
          headers: {
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`),
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Loaded directory:', path, 'Files:', data.files?.length);
        setFileStructure(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to load directory:', response.status, errorText);
        setError('Failed to load directory: ' + response.status);
      }
    } catch (err) {
      console.error('Load directory error:', err);
      setError('Error loading directory: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/files/stats`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // Navigation helpers
  const getParentPath = (path) => {
    if (path === '/') return '/';
    const parts = path.split('/').filter(Boolean);
    parts.pop();
    return parts.length === 0 ? '/' : '/' + parts.join('/');
  };

  const navigateToParent = () => {
    const parentPath = getParentPath(currentPath);
    setCurrentPath(parentPath);
  };

  const getBreadcrumbs = () => {
    if (currentPath === '/') return [{ name: 'Root', path: '/' }];
    
    const parts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Root', path: '/' }];
    
    let accumulatedPath = '';
    parts.forEach(part => {
      accumulatedPath += '/' + part;
      breadcrumbs.push({ name: part, path: accumulatedPath });
    });
    
    return breadcrumbs;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/files/search?query=${encodeURIComponent(searchQuery)}`,
        {
          headers: {
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results);
      }
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files, targetPath = currentPath) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let uploadedCount = 0;
      
      for (let file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('target_path', targetPath);

        console.log('Uploading file:', file.name, 'to path:', targetPath);

        const response = await fetch(`${backendUrl}/api/admin/files/upload`, {
          method: 'POST',
          headers: {
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
          },
          body: formData
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Upload failed:', response.status, errorText);
          throw new Error(`Failed to upload ${file.name}: ${response.status}`);
        }

        const result = await response.json();
        console.log('Upload result:', result);
        uploadedCount++;
      }

      setSuccess(`✅ Successfully uploaded ${uploadedCount} file(s) to ${targetPath}`);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Force refresh of directory and stats with a small delay
      setTimeout(async () => {
        await loadDirectory(currentPath);
        await loadStats();
      }, 500);
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Upload error:', err);
      setError('❌ Upload failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDelete = async (filePath) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/files/delete?file_path=${encodeURIComponent(filePath)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
          }
        }
      );

      if (response.ok) {
        setSuccess('File deleted successfully');
        loadDirectory(currentPath);
        loadStats();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete file');
      }
    } catch (err) {
      setError('Delete failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const renderTreeNode = (node, level = 0) => {
    const isExpanded = expandedFolders.has(node.relative_path);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.relative_path} style={{ marginLeft: `${level * 20}px` }}>
        <div
          className={`flex items-center gap-2 px-2 py-1 hover:bg-gray-700 rounded cursor-pointer ${
            currentPath === node.relative_path ? 'bg-blue-600 text-white' : ''
          }`}
          onClick={() => {
            if (node.type === 'directory') {
              toggleFolder(node.relative_path);
              setCurrentPath(node.relative_path);
            }
          }}
        >
          {hasChildren && (
            isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
          )}
          {!hasChildren && <span className="w-4" />}
          <Folder className={`w-4 h-4 ${currentPath === node.relative_path ? 'text-yellow-300' : 'text-yellow-400'}`} />
          <span className={`text-sm ${currentPath === node.relative_path ? 'font-semibold' : 'text-gray-200'}`}>
            {node.name}
          </span>
          <span className={`text-xs ml-auto ${currentPath === node.relative_path ? 'text-gray-200' : 'text-gray-500'}`}>
            {node.files?.length || 0} files
          </span>
        </div>
        
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderFileCard = (file) => {
    const isImage = file.is_image;

    if (viewMode === 'grid') {
      return (
        <div
          key={file.relative_path}
          className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors cursor-pointer group relative"
          onClick={() => setSelectedFile(file)}
        >
          {isImage ? (
            <div className="aspect-square bg-gray-900 rounded-lg mb-3 overflow-hidden">
              <img
                src={file.relative_path}
                alt={file.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-full h-full items-center justify-center">
                <ImageIcon className="w-12 h-12 text-gray-600" />
              </div>
            </div>
          ) : (
            <div className="aspect-square bg-gray-900 rounded-lg mb-3 flex items-center justify-center">
              <File className="w-12 h-12 text-gray-600" />
            </div>
          )}
          
          <p className="text-sm text-white truncate mb-1">{file.name}</p>
          <p className="text-xs text-gray-400">{file.format}</p>
          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(file.relative_path);
            }}
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 p-2 rounded-lg hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      );
    } else {
      // List view
      return (
        <div
          key={file.relative_path}
          className="bg-gray-800 rounded-lg px-4 py-3 hover:bg-gray-700 transition-colors cursor-pointer flex items-center gap-4 group"
          onClick={() => setSelectedFile(file)}
        >
          {isImage ? (
            <div className="w-10 h-10 bg-gray-900 rounded flex-shrink-0 overflow-hidden">
              <img
                src={file.relative_path}
                alt={file.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-full h-full items-center justify-center">
                <ImageIcon className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 bg-gray-900 rounded flex-shrink-0 flex items-center justify-center">
              <File className="w-6 h-6 text-gray-600" />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{file.name}</p>
            <p className="text-xs text-gray-500 truncate">{file.relative_path}</p>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-400 w-16 text-right">{file.format}</span>
            <span className="text-xs text-gray-400 w-20 text-right">{formatFileSize(file.size)}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(file.relative_path);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 p-2 rounded-lg hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with Stats */}
      <div className="bg-gray-800 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-white">File Manager</h2>
          <div className="flex gap-2">
            <button
              onClick={() => loadDirectory(currentPath)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files.length > 0) {
                  handleFileUpload(Array.from(e.target.files));
                }
              }}
            />
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-700">
          <button
            onClick={() => setCurrentPath('/')}
            className="p-2 hover:bg-gray-700 rounded-lg transition"
            title="Go to root"
          >
            <Home className="w-4 h-4 text-gray-400" />
          </button>
          
          {currentPath !== '/' && (
            <button
              onClick={navigateToParent}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition flex items-center gap-2"
              title="Go up one level"
            >
              <ArrowUp className="w-4 h-4" />
              Up
            </button>
          )}

          <div className="flex items-center gap-1 flex-1 overflow-x-auto">
            {getBreadcrumbs().map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                <button
                  onClick={() => setCurrentPath(crumb.path)}
                  className={`px-3 py-1 rounded-lg transition whitespace-nowrap ${
                    crumb.path === currentPath
                      ? 'bg-blue-600 text-white font-semibold'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Total Files</p>
              <p className="text-white text-xl font-bold">{stats.total_files}</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Total Images</p>
              <p className="text-white text-xl font-bold">{stats.total_images}</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Total Size</p>
              <p className="text-white text-xl font-bold">{stats.total_size_mb} MB</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Files in Current Folder</p>
              <p className="text-white text-xl font-bold">{fileStructure?.files?.length || 0}</p>
            </div>
          </div>
        )}
      </div>

      {/* Search and View Controls */}
      <div className="bg-gray-800 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search images by name..."
              className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              Search
            </button>
          </div>
          
          <div className="flex items-center gap-2 border-l border-gray-700 pl-4">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition ${
                viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition ${
                viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-500 bg-opacity-20 border border-green-500 text-green-300 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 grid grid-cols-12 gap-4 overflow-hidden">
        {/* Folder Tree Sidebar */}
        <div className="col-span-3 bg-gray-800 rounded-lg p-4 overflow-y-auto">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-700">
            <Home className="w-5 h-5 text-gray-400" />
            <h3 className="text-white font-semibold">Directory Tree</h3>
          </div>
          {fileStructure && renderTreeNode(fileStructure)}
        </div>

        {/* File Display Area */}
        <div
          className={`col-span-9 bg-gray-800 rounded-lg p-4 overflow-y-auto ${
            isDragging ? 'border-4 border-dashed border-blue-500 bg-blue-900 bg-opacity-20' : ''
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <div className="text-gray-400 text-lg">Loading files...</div>
            </div>
          ) : searchResults.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">
                  Search Results ({searchResults.length})
                </h3>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="text-sm text-gray-400 hover:text-white"
                >
                  Clear Search
                </button>
              </div>
              <div className={viewMode === 'grid' ? 'grid grid-cols-4 gap-4' : 'space-y-2'}>
                {searchResults.map(file => renderFileCard(file))}
              </div>
            </div>
          ) : fileStructure && fileStructure.files.length > 0 ? (
            <div>
              <h3 className="text-white font-semibold mb-4">
                Files in {currentPath} ({fileStructure.files.length})
              </h3>
              <div className={viewMode === 'grid' ? 'grid grid-cols-4 gap-4' : 'space-y-2'}>
                {fileStructure.files.map(file => renderFileCard(file))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Upload className="w-16 h-16 mb-4" />
              <p className="text-lg mb-2">No files in this directory</p>
              <p className="text-sm">Drag and drop files here or click Upload Files</p>
            </div>
          )}
        </div>
      </div>

      {/* File Details Modal */}
      {selectedFile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedFile(null)}
        >
          <div
            className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">File Details</h3>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {selectedFile.is_image && (
                <div className="mb-4 bg-gray-900 rounded-lg overflow-hidden">
                  <img
                    src={selectedFile.relative_path}
                    alt={selectedFile.name}
                    className="w-full h-auto"
                  />
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm">File Name</p>
                  <p className="text-white font-medium">{selectedFile.name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Full Path</p>
                  <p className="text-white font-mono text-sm break-all">{selectedFile.relative_path}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Format</p>
                  <p className="text-white">{selectedFile.format}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Size</p>
                  <p className="text-white">{formatFileSize(selectedFile.size)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Last Modified</p>
                  <p className="text-white">{new Date(selectedFile.modified).toLocaleString()}</p>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(selectedFile.relative_path);
                    setSuccess('Path copied to clipboard!');
                    setTimeout(() => setSuccess(''), 2000);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                  Copy Path
                </button>
                <button
                  onClick={() => {
                    handleDelete(selectedFile.relative_path);
                    setSelectedFile(null);
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                >
                  Delete File
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;
